import { TagBadge } from "./TagBadge";
import { ScoreRing } from "./ScoreRing";
import { updateItemStatus } from "../api/items";
import { getTagGradient, getTopicImage } from "../utils/tagColors";
import { resolveMediaUrl } from "../api/client";
import type { ItemListEntry } from "../types";

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const now = new Date();
  const diffH = Math.floor((now.getTime() - d.getTime()) / 3600000);
  if (diffH < 1) return "Gerade eben";
  if (diffH < 24) {
    return `Heute, ${d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}`;
  }
  const diffD = Math.floor(diffH / 24);
  if (diffD === 1) return `Gestern, ${d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}`;
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

const SOURCE_CATEGORY_LABELS: Record<string, string> = {
  official_authority: "Behörde",
  news_outlet: "Fachmedium",
  law_firm: "Kanzlei",
  newsletter: "Newsletter",
};

function cleanAuthor(author: string | null): string {
  if (!author) return "Unbekannt";
  const match = author.match(/"?([^"<]+)"?\s*<?/);
  return match?.[1]?.trim() || author.split("@")[0] || "Unbekannt";
}

function summaryToText(summary: string | null): string {
  if (!summary) return "";
  let s = summary.trim();
  // Set-Literal Format: {"- Text1","- Text2"} → aufdröseln
  if (s.startsWith("{") && s.endsWith("}") && s.includes('","')) {
    const inner = s.slice(1, -1);
    const parts = inner.match(/"([^"]*)"/g);
    if (parts) {
      s = parts.map((p) => p.replace(/^"|"$/g, "")).join("\n");
    } else {
      s = inner;
    }
  }
  if (s.startsWith("{") && s.endsWith("}")) {
    s = s.slice(1, -1).trim();
  }
  return s
    .split("\n")
    .map((l) => l.replace(/^[-*•"]\s*/, "").replace(/"\s*$/, "").trim())
    .filter((l) => l.length > 0)
    .join(" ");
}

interface NewsCardProps {
  item: ItemListEntry;
  onSpam?: () => void;
  onSelect?: (id: number) => void;
  sourceLogo?: string | null;
}

export function NewsCard({ item, onSpam, onSelect, sourceLogo }: NewsCardProps) {
  const summaryText = item.teaser || summaryToText(item.summary);

  // Bild-Priorität: KI-Bild > Source-Logo > kuratiertes Stock-Foto
  const imageUrl = item.image_url
    ? resolveMediaUrl(item.image_url)
    : sourceLogo || getTopicImage(item.tags, item.id);

  const handleSpam = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm("Als Spam markieren?")) {
      await updateItemStatus(item.id, "spam");
      onSpam?.();
    }
  };

  const handleClick = () => {
    onSelect?.(item.id);
  };

  return (
    <article
      className={`news-card${!item.is_read ? " unread" : ""}`}
      onClick={handleClick}
    >
      {/* Thumbnail (oben, volle Breite) */}
      <div
        className={`news-card-thumb${sourceLogo && !item.image_url ? " has-logo" : ""}`}
      >
        <img
          src={imageUrl}
          alt=""
          loading="lazy"
          onError={(e) => {
            const fallback = getTopicImage(item.tags, item.id);
            if (e.currentTarget.src !== fallback) {
              e.currentTarget.src = fallback;
            } else {
              e.currentTarget.style.display = "none";
              e.currentTarget.parentElement!.style.background = getTagGradient(item.tags);
            }
          }}
        />
      </div>

      {/* Content (unten) */}
      <div className="news-card-content">
        {/* Top: Tags + Score */}
        <div className="news-card-top">
          <div className="news-card-top-left">
            {item.tags.map((t) => (
              <TagBadge key={t} tag={t} small />
            ))}
          </div>
          <div className="news-card-top-right">
            {item.relevance_score !== null && item.relevance_score > 0 && (
              <ScoreRing score={item.relevance_score} size={30} />
            )}
          </div>
        </div>

        {/* Title */}
        <h3 className="news-card-title">{item.title || "Ohne Titel"}</h3>

        {/* Summary (Fliesstext) */}
        {summaryText && (
          <p className="news-card-summary">{summaryText}</p>
        )}

        {/* Footer: Author + Date + Actions */}
        <div className="news-card-footer">
          <span className="news-card-author">
            {item.source_category && (
              <span className={`source-badge source-${item.source_category}`}>
                {SOURCE_CATEGORY_LABELS[item.source_category] || item.source_category}
              </span>
            )}
            {cleanAuthor(item.author)} · {formatDate(item.published_at)}
          </span>
          <div className="news-card-actions">
            <button
              className="news-card-detail-btn"
              onClick={(e) => { e.stopPropagation(); handleClick(); }}
            >
              Mehr lesen →
            </button>
            <button className="news-card-spam" onClick={handleSpam} title="Als Spam markieren">
              ✕
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}
